import { useState, type ReactNode } from 'react';
import { Icon } from '@iconify/react';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Progress,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { EmptyState } from '../ui';
import {
  MIN_KILOS_FOR_GROWTH,
  OTHERS,
  marketRegions,
  ranking,
  varietyMix,
  type PulseData,
  type RankingCategory,
} from '../../utils/industry-volume/build-pulse';
import { pct, pts, seasonShort, signedPct, toneOf, week } from './format';
import { useVolumeUnit } from './unit';

const Delta = ({ value, children }: { value: number | null; children: string }) => (
  <Chip size='sm' variant='flat' color={toneOf(value)}>
    {children}
  </Chip>
);

// Abre o cierra las filas hijas (variedades de "Otras", países, puertos). La
// tabla vuelve a armar las filas al abrir: se devuelve el foco al botón.
const ExpandButton = ({
  id,
  open,
  onToggle,
  ariaLabel,
  className = '',
  children,
}: {
  id: string;
  open: boolean;
  onToggle: () => void;
  ariaLabel: string;
  className?: string;
  children: ReactNode;
}) => (
  <Button
    size='sm'
    variant='light'
    className={`h-auto min-w-0 gap-1 px-1 ${className}`}
    aria-expanded={open}
    aria-label={ariaLabel}
    startContent={<Icon icon={open ? 'tabler:chevron-down' : 'tabler:chevron-right'} width={14} />}
    data-expand={id}
    onPress={() => {
      onToggle();
      requestAnimationFrame(() =>
        document.querySelector<HTMLElement>(`[data-expand="${CSS.escape(id)}"]`)?.focus()
      );
    }}
  >
    {children}
  </Button>
);

const useOpenSet = () => {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  return { open, toggle };
};

const cardHeader = (title: string, description: string) => (
  <div>
    <h3 className='text-lg font-semibold'>{title}</h3>
    <p className='text-sm text-default-500'>{description}</p>
  </div>
);

// Al abrir "Otras variedades" se listan las que pesan algo; el resto, en una línea.
const MIN_KILOS_OTHER_VARIETY = 10_000;

/** Participación de cada variedad a la fecha, industria y tú. */
export function VarietyMixCard({ data }: { data: PulseData }) {
  const { t } = useTranslation('volume');
  const { v, label, header } = useVolumeUnit();
  const { open, toggle } = useOpenSet();
  const rows = varietyMix(data);
  const prev = seasonShort(data.prev_season);
  const cur = seasonShort(data.season);
  const hasMine = rows.some((r) => r.mineShare != null);
  return (
    <Card shadow='sm'>
      <CardBody className='gap-4 p-6'>
        {cardHeader(
          t('Mix varietal a la fecha'),
          t('Participación de cada variedad en el volumen acumulado a la semana {{week}}, comparada con la misma semana de la temporada {{prev}}.', {
            week: week(data.cutoff_week),
            prev: data.prev_season,
          })
        )}
        {/* En celular se desplaza solo la tabla, no toda la card. */}
        <div className='overflow-x-auto'>
        <Table aria-label={t('Mix varietal')} removeWrapper isStriped isCompact>
          <TableHeader>
            <TableColumn>{t('VARIEDAD')}</TableColumn>
            <TableColumn className='text-right'>{t(header)}</TableColumn>
            <TableColumn className='text-right'>{t('CRECIMIENTO VS {{prev}}', { prev })}</TableColumn>
            <TableColumn>{t('SHARE {{season}} (A LA FECHA)', { season: cur })}</TableColumn>
            <TableColumn className='text-right'>{t('SHARE {{prev}} (MISMA SEM.)', { prev })}</TableColumn>
            <TableColumn className='text-right'>{t('Δ PTS')}</TableColumn>
            <TableColumn className='text-right'>{t('SEMANA PEAK')}</TableColumn>
            <TableColumn className='text-right'>{t('TU MIX')}</TableColumn>
            <TableColumn className='text-right'>{t('TÚ VS IND.')}</TableColumn>
          </TableHeader>
          <TableBody emptyContent={<EmptyState title={t('Sin datos a la fecha.')} />}>
            {rows.flatMap((r) => [
              <TableRow key={r.variety}>
                <TableCell className='font-medium'>
                  {r.variety === OTHERS && r.others.length ? (
                    <ExpandButton
                      id={`variety:${r.variety}`}
                      open={open.has(r.variety)}
                      onToggle={() => toggle(r.variety)}
                      ariaLabel={t('Ver las otras variedades')}
                      className='font-medium'
                    >
                      {t('Otras variedades')}
                    </ExpandButton>
                  ) : r.variety === OTHERS ? (
                    t('Otras variedades')
                  ) : (
                    r.variety
                  )}
                </TableCell>
                <TableCell className='text-right'>{v(r.kilos)}</TableCell>
                <TableCell className='text-right'>
                  <Delta value={r.growth}>{signedPct(r.growth)}</Delta>
                </TableCell>
                <TableCell>
                  <div className='flex min-w-[140px] items-center gap-2'>
                    <Progress aria-label={r.variety} size='sm' value={r.shareCur * 100} className='max-w-[100px]' />
                    <span className='text-xs text-default-500'>{pct(r.shareCur)}</span>
                  </div>
                </TableCell>
                <TableCell className='text-right'>{pct(r.sharePrev)}</TableCell>
                <TableCell className='text-right'>
                  <Delta value={r.deltaPts}>{pts(r.deltaPts)}</Delta>
                </TableCell>
                <TableCell className='whitespace-nowrap text-right'>
                  {week(r.peakCur)}
                  <span className='text-xs text-default-400'> · {prev}: {week(r.peakPrev)}</span>
                </TableCell>
                <TableCell className='text-right'>{hasMine ? pct(r.mineShare) : '—'}</TableCell>
                <TableCell className='text-right'>
                  {hasMine ? <Delta value={r.mineVsIndustryPts}>{pts(r.mineVsIndustryPts)}</Delta> : '—'}
                </TableCell>
              </TableRow>,
              ...(open.has(r.variety)
                ? r.others.filter((o) => o.kilos >= MIN_KILOS_OTHER_VARIETY).map((o) => (
                    <TableRow key={`${r.variety}/${o.variety}`}>
                      <TableCell className='pl-8 text-default-600'>{o.variety}</TableCell>
                      <TableCell className='text-right'>{v(o.kilos)}</TableCell>
                      <TableCell className='text-right'>
                        <Delta value={o.growth}>{signedPct(o.growth)}</Delta>
                      </TableCell>
                      <TableCell>
                        <span className='text-xs text-default-500'>{pct(o.shareCur)}</span>
                      </TableCell>
                      <TableCell className='text-right'>{pct(o.sharePrev)}</TableCell>
                      <TableCell className='text-right'>
                        <Delta value={o.deltaPts}>{pts(o.deltaPts)}</Delta>
                      </TableCell>
                      <TableCell className='text-right'>—</TableCell>
                      <TableCell className='text-right'>{hasMine ? pct(o.mineShare) : '—'}</TableCell>
                      <TableCell className='text-right'>
                        {hasMine ? <Delta value={o.mineVsIndustryPts}>{pts(o.mineVsIndustryPts)}</Delta> : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                : []),
              ...(open.has(r.variety) && r.others.some((o) => o.kilos < MIN_KILOS_OTHER_VARIETY)
                ? [
                    <TableRow key={`${r.variety}/resto`}>
                      {[
                        <TableCell key='name' className='pl-8 text-xs text-default-500'>
                          {t('y {{count}} más con menos de {{min}} {{unit}} cada una', {
                            count: r.others.filter((o) => o.kilos < MIN_KILOS_OTHER_VARIETY).length,
                            min: v(MIN_KILOS_OTHER_VARIETY),
                            unit: t(label),
                          })}
                        </TableCell>,
                        ...Array.from({ length: 8 }, (_, i) => <TableCell key={i}>{''}</TableCell>),
                      ]}
                    </TableRow>,
                  ]
                : []),
            ])}
          </TableBody>
        </Table>
        </div>
      </CardBody>
    </Card>
  );
}

const CATEGORIES: { key: RankingCategory; label: string }[] = [
  { key: 'volume', label: 'Top 10 volumen' },
  { key: 'growth', label: 'Top 10 crecimiento' },
  { key: 'share', label: 'Top 10 share ganado' },
  { key: 'nonChina', label: 'Top 10 fuera de China' },
  { key: 'air', label: 'Top 10 aéreo' },
];

/** Rankings a la fecha, con la fila de la exportadora del usuario siempre a la vista. */
export function ExporterRankingCard({ data }: { data: PulseData }) {
  const { t } = useTranslation('volume');
  const { v, label, header } = useVolumeUnit();
  const unitHeader = t(header);
  const [category, setCategory] = useState<RankingCategory>('volume');
  const categories = [
    ...CATEGORIES,
    ...(data.top_varieties ?? []).slice(0, 3).map((v) => ({
      key: `variety:${v}` as RankingCategory,
      label: `Top 10 ${v.charAt(0)}${v.slice(1).toLowerCase()}`,
    })),
  ];
  const r = ranking(data, category);
  const cur = seasonShort(data.season);
  const prev = seasonShort(data.prev_season);
  const metricLabel =
    category === 'air'
      ? t('AÉREO {{unit}}', { unit: unitHeader })
      : category === 'nonChina'
        ? t('FUERA DE CHINA {{unit}}', { unit: unitHeader })
        : category.startsWith('variety:')
          ? `${category.slice(8)} ${unitHeader}`
          : t('ACUM. SEM {{week}} {{unit}}', { week: week(data.cutoff_week), unit: unitHeader });
  const rows = [...r.rows, ...(r.mine ? [r.mine] : [])];

  return (
    <Card shadow='sm'>
      <CardBody className='gap-4 p-6'>
        {cardHeader(
          t('Ranking de exportadoras'),
          t('Volumen acumulado a la semana {{week}} por exportadora y variación respecto de la misma semana de {{prev}}. Tu exportadora se muestra siempre, aunque no esté en el top 10.', {
            week: week(data.cutoff_week),
            prev: data.prev_season,
          })
        )}
        <Select
          size='sm'
          className='max-w-xs'
          label={t('Ranking')}
          selectedKeys={[category]}
          disallowEmptySelection
          onSelectionChange={(keys) => setCategory(Array.from(keys)[0] as RankingCategory)}
        >
          {categories.map((c) => (
            <SelectItem key={c.key}>{t(c.label)}</SelectItem>
          ))}
        </Select>
        {category === 'growth' && (
          <p className='text-xs text-default-500'>
            {t('Solo exportadoras con más de {{min}} {{unit}} a la fecha.', { min: v(MIN_KILOS_FOR_GROWTH), unit: t(label) })}
          </p>
        )}
        {/* En celular se desplaza solo la tabla, no toda la card. */}
        <div className='overflow-x-auto'>
        <Table aria-label={t('Ranking de exportadoras')} removeWrapper isStriped isCompact>
          <TableHeader>
            <TableColumn className='text-right'>#</TableColumn>
            <TableColumn>{t('EXPORTADORA')}</TableColumn>
            <TableColumn className='text-right'>{metricLabel}</TableColumn>
            <TableColumn className='text-right'>{t('{{prev}} (MISMA SEM.)', { prev })}</TableColumn>
            <TableColumn className='text-right'>{t('CRECIMIENTO VS {{prev}}', { prev })}</TableColumn>
            <TableColumn className='text-right'>{t('SHARE {{season}} (A LA FECHA)', { season: cur })}</TableColumn>
          </TableHeader>
          <TableBody emptyContent={<EmptyState title={t('Sin exportadoras en esta categoría.')} />}>
            {[
              ...rows.map((row) => (
                <TableRow key={row.key} className={row.mine ? 'bg-primary-50 dark:bg-primary-50/10' : undefined}>
                  <TableCell className='text-right'>{row.position}</TableCell>
                  <TableCell className='font-medium'>
                    {row.name}
                    {row.mine && (
                      <Chip size='sm' variant='flat' className='ml-2'>
                        {t('tú')}
                      </Chip>
                    )}
                  </TableCell>
                  <TableCell className='text-right'>
                    {v(category === 'air' || category === 'nonChina' || category.startsWith('variety:') ? row.metric : row.cur)}
                  </TableCell>
                  <TableCell className='text-right'>{v(row.prev)}</TableCell>
                  <TableCell className='text-right'>
                    <Delta value={row.growth}>{signedPct(row.growth)}</Delta>
                  </TableCell>
                  <TableCell className='text-right'>{pct(row.share)}</TableCell>
                </TableRow>
              )),
              <TableRow key='__industry__'>
                <TableCell>{' '}</TableCell>
                <TableCell className='font-semibold'>
                  {t('Industria')}
                  <span className='block text-xs font-normal text-default-500'>
                    {t('{{count}} exportadoras', { count: r.eligible })}
                  </span>
                </TableCell>
                <TableCell className='text-right font-semibold'>{v(r.industryCur)}</TableCell>
                <TableCell className='text-right font-semibold'>{v(r.industryPrev)}</TableCell>
                <TableCell className='text-right'>
                  <Delta value={r.industryPrev ? r.industryCur / r.industryPrev - 1 : null}>
                    {signedPct(r.industryPrev ? r.industryCur / r.industryPrev - 1 : null)}
                  </Delta>
                </TableCell>
                <TableCell className='text-right font-semibold'>100%</TableCell>
              </TableRow>,
            ]}
          </TableBody>
        </Table>
        </div>
      </CardBody>
    </Card>
  );
}

/** Regiones y países de destino a la fecha; cada región se despliega en sus países. */
export function MarketsCard({ data }: { data: PulseData }) {
  const { t } = useTranslation('volume');
  const { v, header } = useVolumeUnit();
  const { open, toggle } = useOpenSet();
  const regions = marketRegions(data);
  const prev = seasonShort(data.prev_season);
  const cur = seasonShort(data.season);
  const hasMine = regions.some((r) => r.mineShare != null);
  type Country = (typeof regions)[number]['countries'][number];
  const cells = (r: (typeof regions)[number] | Country | Country['ports'][number]) => [
    <TableCell key='k' className='text-right'>{v(r.kilos)}</TableCell>,
    <TableCell key='g' className='text-right'><Delta value={r.growth}>{signedPct(r.growth)}</Delta></TableCell>,
    <TableCell key='s'>
      <div className='flex min-w-[140px] items-center gap-2'>
        <Progress aria-label='share' size='sm' value={r.shareCur * 100} className='max-w-[100px]' />
        <span className='text-xs text-default-500'>{pct(r.shareCur)}</span>
      </div>
    </TableCell>,
    <TableCell key='p' className='text-right'>{pct(r.sharePrev)}</TableCell>,
    <TableCell key='d' className='text-right'><Delta value={r.deltaPts}>{pts(r.deltaPts)}</Delta></TableCell>,
    <TableCell key='m' className='text-right'>{hasMine ? pct(r.mineShare) : '—'}</TableCell>,
    <TableCell key='v' className='text-right'>
      {hasMine ? <Delta value={r.mineVsIndustryPts}>{pts(r.mineVsIndustryPts)}</Delta> : '—'}
    </TableCell>,
  ];

  return (
    <Card shadow='sm'>
      <CardBody className='gap-4 p-6'>
        {cardHeader(
          t('Mercados de destino'),
          t('Participación de cada región, país y puerto de descarga en el volumen acumulado a la semana {{week}}, comparada con la misma semana de {{prev}}. Expande una región para ver sus países y puertos.', {
            week: week(data.cutoff_week),
            prev: data.prev_season,
          })
        )}
        {/* En celular se desplaza solo la tabla, no toda la card. */}
        <div className='overflow-x-auto'>
        <Table aria-label={t('Regiones y países de destino')} removeWrapper isStriped isCompact>
          <TableHeader>
            <TableColumn>{t('REGIÓN · PAÍS · PUERTO')}</TableColumn>
            <TableColumn className='text-right'>{t(header)}</TableColumn>
            <TableColumn className='text-right'>{t('CRECIMIENTO VS {{prev}}', { prev })}</TableColumn>
            <TableColumn>{t('SHARE {{season}} (A LA FECHA)', { season: cur })}</TableColumn>
            <TableColumn className='text-right'>{t('SHARE {{prev}} (MISMA SEM.)', { prev })}</TableColumn>
            <TableColumn className='text-right'>{t('Δ PTS')}</TableColumn>
            <TableColumn className='text-right'>{t('TU MIX')}</TableColumn>
            <TableColumn className='text-right'>{t('TÚ VS IND.')}</TableColumn>
          </TableHeader>
          <TableBody emptyContent={<EmptyState title={t('Sin datos a la fecha.')} />}>
            {regions.flatMap((r) => [
              <TableRow key={r.region}>
                {[
                  <TableCell key='name' className='font-medium'>
                    <ExpandButton
                      id={`region:${r.region}`}
                      open={open.has(r.region)}
                      onToggle={() => toggle(r.region)}
                      ariaLabel={t('Ver países de {{region}}', { region: r.region })}
                      className='font-medium'
                    >
                      {r.region}
                    </ExpandButton>
                    <span className='block pl-5 text-xs text-default-500'>
                      {r.activeCountries === 1
                        ? t('1 país')
                        : t('{{count}} países', { count: r.activeCountries })}
                    </span>
                  </TableCell>,
                  ...cells(r),
                ]}
              </TableRow>,
              ...(open.has(r.region)
                ? r.countries.flatMap((c) => {
                    const countryKey = `${r.region}/${c.country}`;
                    return [
                      <TableRow key={countryKey}>
                        {[
                          <TableCell key='name' className='pl-6 text-default-600'>
                            <ExpandButton
                              id={`country:${countryKey}`}
                              open={open.has(countryKey)}
                              onToggle={() => toggle(countryKey)}
                              ariaLabel={t('Ver puertos de {{country}}', { country: c.country })}
                            >
                              {c.country}
                            </ExpandButton>
                          </TableCell>,
                          ...cells(c),
                        ]}
                      </TableRow>,
                      ...(open.has(countryKey)
                        ? c.ports.map((port) => (
                            <TableRow key={`${countryKey}/${port.port}`}>
                              {[
                                <TableCell key='name' className='pl-14 text-xs text-default-500'>{port.port}</TableCell>,
                                ...cells(port),
                              ]}
                            </TableRow>
                          ))
                        : []),
                    ];
                  })
                : []),
            ])}
          </TableBody>
        </Table>
        </div>
      </CardBody>
    </Card>
  );
}
